import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { ValidationStatusComponent } from 'utils/Helper';
import { useValidationStatus } from 'hooks/useValidationStatus';
import { useUser } from 'routes/UserContext';
import './InteractiveValidationStatus.scss';

export const InteractiveValidationStatus = ({
  reportType,
  reportId,
  currentStatus,
  onStatusChange,
  disabled = false,
}) => {
  const [openStatusSelect, setOpenStatusSelect] = useState({
    open: false,
    direction: 'bottom',
    ready: false
  });
  const [statusOptions, setStatusOptions] = useState([]);
  const statusModalRef = useRef(null);

  const { user } = useUser();
  const {
    loading: statusLoading,
    fetchValidationStatuses,
    updateValidationStatus
  } = useValidationStatus();

  const canChangeStatus = user?.permissions?.change_settings;
  const isDisabled = disabled || !canChangeStatus;

  const handleStatusClick = async (event) => {
    event.stopPropagation();

    if (isDisabled) return;

    if (openStatusSelect.open) {
      setOpenStatusSelect({ open: false, direction: 'bottom', ready: false });
      return;
    }

    setOpenStatusSelect({ open: true, direction: 'bottom', ready: false });

    if (reportType && reportId) {
      try {
        const statuses = await fetchValidationStatuses(reportType, reportId);
        if (statuses && statuses.length > 0) {
          setStatusOptions(statuses);
        } else {
          setStatusOptions([]);
        }
      } catch (error) {
        console.error('Error fetching statuses:', error);
        setStatusOptions([]);
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateValidationStatus(reportType, reportId, newStatus.value);

      setOpenStatusSelect({ open: false, direction: 'bottom', ready: false });
      setStatusOptions([]);

      if (onStatusChange) {
        onStatusChange(reportId, {
          ...newStatus,
          status_manually_changed: true
        });
      }

    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleClickOutside = (event) => {
    const select = document.querySelector('.interactive-status-select');
    if (select && !select.contains(event.target)) {
      setOpenStatusSelect({ open: false, direction: 'bottom', ready: false });
    }
  };

  useLayoutEffect(() => {
    if (openStatusSelect.open && statusModalRef.current && !openStatusSelect.ready) {
      const cell = document.querySelector('.interactive-status-cell');
      const rect = cell?.getBoundingClientRect();
      const modalRect = statusModalRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - (rect?.bottom || 0);
      const direction = spaceBelow < modalRect.height ? 'top' : 'bottom';
      setOpenStatusSelect((prev) => ({ ...prev, direction, ready: true }));
    }
  }, [openStatusSelect.open, openStatusSelect.ready]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!currentStatus) {
    return <span>-</span>;
  }

  return (
    <div
      className={`interactive-status-cell ${isDisabled ? 'disabled' : ''}`}
      onClick={handleStatusClick}
    >
      <ValidationStatusComponent status={currentStatus} />

      {openStatusSelect.open && (
        <div
          ref={statusModalRef}
          className={`interactive-status-select${openStatusSelect.direction === 'top' ? ' interactive-status-select--top' : ''}`}
          style={!openStatusSelect.ready ? { visibility: 'hidden', pointerEvents: 'none' } : {}}
        >
          {statusLoading ? (
            <div className="interactive-status-option interactive-status-option--loading">
              Loading statuses...
            </div>
          ) : statusOptions.length > 0 ? (
            statusOptions
              .filter(status => status.value !== currentStatus?.value)
              .map((status) => (
                <div
                  key={status.value}
                  className="interactive-status-option"
                  onClick={() => handleStatusChange(status)}
                >
                  <ValidationStatusComponent status={{
                    ...status,
                    status_manually_changed: false
                  }} />
                </div>
              ))
          ) : (
            <div className="interactive-status-option interactive-status-option--no-data">
              No statuses available
            </div>
          )}
        </div>
      )}
    </div>
  );
};